import i18n from 'i18next';

/**
 * Every failed API call rejects with an ApiError.
 *
 * The BE emits three error body shapes, and `parseErrorBody` accepts all of
 * them (FE plan §3.3):
 *
 *   1. handler errors    `{ message: string,   correlationId, error: { code, params } }`
 *   2. middleware errors `{ message: string[], correlationId, error?: { code, params } }`
 *      (auth, CSRF, role gate)
 *   3. 501 stubs         `{ error: "not implemented", reason }` — `error` is a
 *      string here, not an object
 *
 * `userMessage` is what the UI shows: the translated coded error when there
 * is one, else the BE's prose, else a generic fallback. `message` stays the
 * dev-facing "API Error <status>: <body>" string for logs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params: Record<string, unknown>;
  readonly correlationId?: string;
  readonly userMessage: string;
  readonly endpoint: string;
  readonly method: string;

  constructor(init: {
    status: number;
    code: string;
    params?: Record<string, unknown>;
    correlationId?: string;
    userMessage: string;
    endpoint: string;
    method: string;
    rawBody: string;
  }) {
    super(`API Error ${init.status}: ${init.rawBody}`);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.params = init.params ?? {};
    this.correlationId = init.correlationId;
    this.userMessage = init.userMessage;
    this.endpoint = init.endpoint;
    this.method = init.method;
  }

  get isNotImplemented(): boolean {
    return this.code === NOT_IMPLEMENTED;
  }
}

export const NOT_IMPLEMENTED = 'NOT_IMPLEMENTED';
export const UNKNOWN = 'UNKNOWN';

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export interface ParsedErrorBody {
  code: string;
  params: Record<string, unknown>;
  correlationId?: string;
  /** BE prose, when the body carried any. */
  prose?: string;
}

/** Normalises the three BE error shapes. Never throws. */
export function parseErrorBody(status: number, rawBody: string): ParsedErrorBody {
  let json: unknown;
  try {
    json = JSON.parse(rawBody);
  } catch {
    return { code: status === 501 ? NOT_IMPLEMENTED : UNKNOWN, params: {} };
  }
  if (!json || typeof json !== 'object') {
    return { code: UNKNOWN, params: {} };
  }
  const body = json as Record<string, unknown>;
  const correlationId =
    typeof body.correlationId === 'string'
      ? body.correlationId
      : typeof body.CorrelationId === 'string'
        ? body.CorrelationId
        : undefined;

  // Shape 3 — 501 stub: `error` is a string, the explanation is in `reason`.
  if (typeof body.error === 'string') {
    return {
      code: status === 501 ? NOT_IMPLEMENTED : UNKNOWN,
      params: {},
      correlationId,
      prose: typeof body.reason === 'string' ? body.reason : body.error,
    };
  }

  // Shapes 1 + 2 — `message` is a string or a string[].
  let prose: string | undefined;
  if (typeof body.message === 'string') prose = body.message;
  else if (Array.isArray(body.message)) {
    prose = body.message.filter((m): m is string => typeof m === 'string').join(' ');
  }

  const errBlock = body.error as { code?: unknown; params?: unknown } | undefined;
  const code = typeof errBlock?.code === 'string' && errBlock.code ? errBlock.code : UNKNOWN;
  const params =
    errBlock?.params && typeof errBlock.params === 'object' ? (errBlock.params as Record<string, unknown>) : {};

  return { code, params, correlationId, prose };
}

/**
 * The string shown to the operator. A known code is translated from the
 * `errors` namespace (with params interpolated); an unknown code falls back
 * to the BE's prose, then to the generic `errors:UNKNOWN`.
 */
export function userMessageFor(parsed: ParsedErrorBody): string {
  // `t` returns undefined before i18next has initialised (very early boot,
  // tests) — never let that become an empty error modal.
  const generic =
    (i18n.t('errors:UNKNOWN', { defaultValue: 'Something went wrong.' }) as string | undefined) ||
    'Something went wrong.';
  if (parsed.code !== UNKNOWN && parsed.code !== NOT_IMPLEMENTED) {
    const key = `errors:${parsed.code}`;
    if (i18n.exists(key)) return i18n.t(key, parsed.params) as string;
  }
  return parsed.prose || generic;
}
